import React from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Rocket, Radar, Users as UsersIcon, GitBranch, Send, Sparkles, FileText,
  Handshake, Users2, LinkIcon, Star, BarChart3, Activity, Brain, Settings as Cog,
  ArrowLeft, LogOut, ShieldCheck, Zap, Megaphone,
} from "lucide-react";

const nav = [
  { to: "/growth",                 label: "Command Center",     icon: Rocket, end: true },
  { to: "/growth/opportunities",   label: "Opportunity Radar",  icon: Radar },
  { to: "/growth/leads",           label: "Leads",              icon: UsersIcon },
  { to: "/growth/pipeline",        label: "Pipeline",           icon: GitBranch },
  { to: "/growth/outreach",        label: "Outreach",           icon: Send },
  { to: "/growth/activation",      label: "Customer Activation",icon: Sparkles },
  { to: "/growth/social",          label: "Social Posts",       icon: Megaphone },
  { to: "/growth/content",         label: "Content Engine",     icon: FileText },
  { to: "/growth/partnerships",    label: "Partnerships",       icon: Handshake },
  { to: "/growth/communities",     label: "Communities",        icon: Users2 },
  { to: "/growth/affiliates",      label: "Affiliates",         icon: LinkIcon },
  { to: "/growth/testimonials",    label: "Testimonials",       icon: Star },
  { to: "/growth/analytics",       label: "Analytics",          icon: BarChart3 },
  { to: "/growth/agent-log",       label: "Agent Activity",     icon: Activity },
  { to: "/growth/insights",        label: "AI Insights",        icon: Brain },
  { to: "/growth/settings",        label: "Growth Settings",    icon: Cog },
];

export default function GrowthLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex" data-testid="growth-layout">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="px-4 py-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg">
              <Rocket className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-extrabold text-white text-sm leading-tight">Growth Center</p>
              <p className="text-[10px] uppercase tracking-widest text-violet-300">1,000 by Jan 1</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`growth-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2 text-sm font-semibold transition-colors border-l-2 ${
                  isActive
                    ? "bg-indigo-500/10 text-white border-indigo-400"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/50 border-transparent"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800 space-y-2">
          <Link to="/admin" className="block">
            <Button variant="outline" className="w-full justify-start border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white" data-testid="growth-back-to-admin">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to PPT Admin
            </Button>
          </Link>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="growth-logout"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-slate-900/80 backdrop-blur border-b border-slate-800 px-6 py-3 flex items-center justify-between" data-testid="growth-header">
          <div className="flex items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 text-[10px] uppercase tracking-widest font-extrabold">
              <Zap className="h-3 w-3" /> Growth Center
            </span>
            <span className="text-slate-500">·</span>
            <Link to="/admin" className="text-slate-400 hover:text-white">Back to PPT Admin</Link>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "super_admin" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 text-[10px] font-extrabold uppercase tracking-widest">
                <ShieldCheck className="h-3 w-3" /> Super Admin
              </span>
            )}
            <span className="text-xs text-slate-400">{user?.email}</span>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
